import { createTheme } from '@mui/material/styles';
import { fiFI as coreLocalization } from '@mui/material/locale';
import { fiFI as pickersLocalization } from '@mui/x-date-pickers/locales';
import { COLORS } from './colors';
import type { TypographyOptions } from '@mui/material/styles/createTypography';

/**
 * Theme extras are the extra properties that are added to the theme and theme types
 *
 * See definitions in src/types/mui-theme.d.ts and src/types/emotion-theme.d.ts
 */
export type ThemeExtras = {
  mainColor: string;
  mainColorMedium: string;
  mainColorDark: string;
  mainColorLight: string;
  mainColorLightMedium: string;
  mainColorLightDark: string;
  mainColorTransparent: string;
  white: string;
};

const themePalette = (chain: 'intersport' | 'budgetsport') => ({
  primary: {
    main: chain === 'intersport' ? COLORS.INTER_PRIMARY : COLORS.BUDGET_PRIMARY,
    dark: chain === 'intersport' ? COLORS.INTER_PRIMARY_DARK : COLORS.BUDGET_PRIMARY_DARK,
    medium: chain === 'intersport' ? COLORS.INTER_PRIMARY_MEDIUM : COLORS.BUDGET_PRIMARY_MEDIUM,
    light: chain === 'intersport' ? COLORS.INTER_PRIMARY_LIGHT : COLORS.BUDGET_PRIMARY_LIGHT,
    lightMedium:
      chain === 'intersport'
        ? COLORS.INTER_PRIMARY_LIGHT_MEDIUM
        : COLORS.BUDGET_PRIMARY_LIGHT_MEDIUM,
    lightDark:
      chain === 'intersport' ? COLORS.INTER_PRIMARY_LIGHT_DARK : COLORS.BUDGET_PRIMARY_LIGHT_DARK,
    transparent:
      chain === 'intersport' ? COLORS.INTER_PRIMARY_TRANSPARENT : COLORS.BUDGET_PRIMARY_TRANSPARENT,
  },
  error: {
    main: COLORS.ERROR,
    dark: COLORS.ERROR_DARK,
  },
  success: {
    main: COLORS.SUCCESS,
    dark: COLORS.SUCCESS_DARK,
  },
});

const themeComponents = {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
          backgroundColor: COLORS.BG_LIGHT_MAIN,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          padding: '0 !important',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { color: 'success' },
              style: {
                backgroundColor: '#CBF7D1',
                color: COLORS.BLACK,
              },
            },
            {
              props: { color: 'info' },
              style: {
                backgroundColor: COLORS.BABY_BLUE,
                color: COLORS.BLACK,
              },
            },
            {
              props: { color: 'warning' },
              style: {
                backgroundColor: '#FCE7CD',
                color: COLORS.BLACK,
              },
            },
            {
              props: { color: 'error' },
              style: {
                backgroundColor: COLORS.PINK,
                color: COLORS.BLACK,
              },
            },
          ],
        },
      },
    },
  },
};

const themes = {
  intersport: createTheme(
    {
      ...themeComponents,
      mainColorDark: COLORS.INTER_PRIMARY_DARK,
      mainColorMedium: COLORS.INTER_PRIMARY_MEDIUM,
      mainColor: COLORS.INTER_PRIMARY,
      mainColorLight: COLORS.INTER_PRIMARY_LIGHT,
      mainColorLightMedium: COLORS.INTER_PRIMARY_LIGHT_MEDIUM,
      mainColorLightDark: COLORS.INTER_PRIMARY_LIGHT_DARK,
      mainColorTransparent: COLORS.INTER_PRIMARY_TRANSPARENT,
      white: COLORS.WHITE,
      palette: themePalette('intersport'),
      typography: {
        custom1: {
          fontSize: '0.75rem',
        },
        custom2: {
          fontSize: '0.875rem',
        },
        custom3: {
          fontSize: '1.5rem',
          fontWeight: 700,
        },
        tiny: {
          fontSize: '0.625rem',
        },
      } as TypographyOptions,
    },
    coreLocalization,
    pickersLocalization
  ),
  budgetsport: createTheme(
    {
      ...themeComponents,
      mainColorDark: COLORS.BUDGET_PRIMARY_DARK,
      mainColorMedium: COLORS.BUDGET_PRIMARY_MEDIUM,
      mainColor: COLORS.BUDGET_PRIMARY,
      mainColorLight: COLORS.BUDGET_PRIMARY_LIGHT,
      mainColorLightMedium: COLORS.BUDGET_PRIMARY_LIGHT_MEDIUM,
      mainColorLightDark: COLORS.BUDGET_PRIMARY_LIGHT_DARK,
      mainColorTransparent: COLORS.BUDGET_PRIMARY_TRANSPARENT,
      white: COLORS.WHITE,
      palette: themePalette('budgetsport'),
    },
    coreLocalization,
    pickersLocalization
  ),
};

if (themes.intersport.components) {
  themes.intersport.components.MuiMenuItem = {
    styleOverrides: {
      root: {
        '&:hover': {
          background: COLORS.INTER_HOVER,
        },
      },
    },
  };

  themes.intersport.components.MuiTableRow = {
    styleOverrides: {
      root: {
        '&.MuiTableRow-hover:hover': {
          backgroundColor: COLORS.INTER_HOVER,
        },
      },
    },
  };
}

if (themes.budgetsport.components) {
  themes.budgetsport.components.MuiMenuItem = {
    styleOverrides: {
      root: {
        '&:hover': {
          background: COLORS.BUDGET_HOVER,
        },
      },
    },
  };

  themes.budgetsport.components.MuiTableRow = {
    styleOverrides: {
      root: {
        '&.MuiTableRow-hover:hover': {
          backgroundColor: COLORS.BUDGET_HOVER,
        },
      },
    },
  };
}

type MergeCommonWithOptionalExclusives<T, U> = { [K in Extract<keyof T, keyof U>]: T[K] } & Partial<
  Omit<T, keyof U> & Omit<U, keyof T>
>;

export type CommonTheme = MergeCommonWithOptionalExclusives<
  (typeof themes)['intersport'],
  (typeof themes)['budgetsport']
>;

export { themes };
